import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { loadLiff } from '@/features/line-app/liff';

/**
 * A LIFF diagnostic page. No connection, no Official Account, no account at all.
 *
 * It exists for two questions that cannot be answered without a real channel and
 * a real phone:
 *
 *  1. The §3 spike: does `liff.login()` work in an ordinary browser, outside
 *     LINE? If it does, the website's "Continue with LINE" button can use the
 *     MINI App channel and the separate LINE Login channel is never needed —
 *     one less thing every shop has to create.
 *  2. Whether Stripe Checkout returns cleanly into the MINI App on iOS and
 *     Android, which decides how payment is opened.
 *
 * Deliberately inert: it talks to LINE's SDK and to nothing of ours. There is no
 * API call, no token is sent anywhere, and the LIFF ID is typed in rather than
 * looked up, so it cannot expose another shop's configuration. Safe in
 * production, which it has to be — LIFF requires an https endpoint, so this
 * cannot be tested on localhost.
 */
export const Route = createFileRoute('/line/spike')({
  validateSearch: (s: Record<string, unknown>): { liffId?: string } =>
    typeof s.liffId === 'string' ? { liffId: s.liffId } : {},
  component: LiffSpikePage,
});

type Line = { label: string; value: string };

function LiffSpikePage() {
  const search = Route.useSearch();
  const [liffId, setLiffId] = useState(search.liffId ?? '');
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);

  const add = (label: string, value: unknown) =>
    setLines((prev) => [...prev, { label, value: String(value) }]);

  const run = async () => {
    setLines([]);
    setBusy(true);
    try {
      add('user agent', navigator.userAgent);
      const liff = await loadLiff();
      add('sdk loaded', 'yes');
      await liff.init({ liffId: liffId.trim() });
      add('liff.init', 'ok');
      add('isInClient (inside the LINE app)', liff.isInClient());
      add('isLoggedIn', liff.isLoggedIn());

      if (liff.isLoggedIn()) {
        const token = liff.getIDToken();
        add('getIDToken', token ? `present, ${token.length} chars` : 'null');
        try {
          const profile = await liff.getProfile();
          add('getProfile.userId', profile.userId);
          add('getProfile.displayName', profile.displayName ?? '(none)');
        } catch (err) {
          add('getProfile', `threw: ${err instanceof Error ? err.message : 'unknown'}`);
        }
        try {
          add('getFriendship.friendFlag', (await liff.getFriendship()).friendFlag);
        } catch (err) {
          add('getFriendship', `threw: ${err instanceof Error ? err.message : 'unknown'}`);
        }
      } else {
        add('note', 'not logged in — press "Try liff.login()" below, which is the spike');
      }
    } catch (err) {
      add('ERROR', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const login = async () => {
    try {
      const liff = await loadLiff();
      await liff.init({ liffId: liffId.trim() });
      // The spike itself. Inside LINE this is a no-op; in an ordinary browser
      // it either redirects to access.line.me and comes back, or it does
      // nothing — and which of those happens is the answer.
      liff.login({ redirectUri: window.location.href });
    } catch (err) {
      add('login ERROR', err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <main className="mx-auto max-w-lg px-4 py-6 font-mono text-xs">
      <h1 className="mb-1 font-sans text-base font-semibold">LIFF diagnostic</h1>
      <p className="mb-4 font-sans text-sm text-neutral-600">
        Enter a LIFF ID and press Run. Nothing is sent to Business Box.
      </p>

      <input
        value={liffId}
        onChange={(e) => setLiffId(e.target.value)}
        placeholder="1234567890-AbcdEfgh"
        autoComplete="off"
        spellCheck={false}
        className="mb-3 w-full rounded border border-neutral-300 px-3 py-2 text-base"
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={run}
          disabled={busy || liffId.trim().length === 0}
          className="min-h-11 rounded bg-neutral-900 px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          Run
        </button>
        <button
          type="button"
          onClick={login}
          disabled={liffId.trim().length === 0}
          className="min-h-11 rounded border border-neutral-300 px-4 text-sm font-medium disabled:opacity-40"
        >
          Try liff.login()
        </button>
      </div>

      {lines.length > 0 ? (
        <dl className="space-y-1 rounded border border-neutral-200 bg-neutral-50 p-3">
          {lines.map((l) => (
            <div key={`${l.label}:${l.value}`} className="break-words">
              <dt className="inline text-neutral-500">{l.label}: </dt>
              <dd className="inline">{l.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </main>
  );
}
